import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api/client";
import { getRoleBadgeColor } from "@/mocks/auth";

// ── types ─────────────────────────────────────────────────────────────────────
type ThresholdConfig = {
  highAlert: number;
  mediumAlert: number;
  lowAlert: number;
};

type UserRead = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "Active" | "Invited" | "Suspended";
  organizationName: string;
  is_active?: boolean;
  full_name?: string;
};

type UserFormData = Partial<UserRead> & { password?: string; confirmPassword?: string };

type District = {
  id: string;
  name: string;
  region: string;
  population: number;
  status: "Active" | "Inactive";
  lastSync: string;
  area_km2?: number;
};

type APIKeyCategory = "weather" | "maps" | "topography" | "internal" | "other";

type APIKey = {
  id: string;
  name: string;
  key: string;
  category: APIKeyCategory;
  provider: string;
  environment: "production" | "staging" | "development";
  createdAt: string;
  lastUsed: string;
  status: "Active" | "Revoked";
};

type Organization = {
  id: string;
  name: string;
  type: "Government" | "NGO" | "Research" | "Private";
  country: string;
  contactEmail: string;
  userCount: number;
  districtCount: number;
  status: "Active" | "Suspended";
  createdAt: string;
};

type MalariaCase = {
  id: string;
  district_id: string;
  district_name: string;
  week_start: string;
  confirmed_cases: number;
  suspected_cases: number | null;
  deaths: number | null;
  reported_at: string;
};

type SystemHealth = {
  api_status: string;
  model_version_2w: string | null;
  model_version_4w: string | null;
  model_version_12w: string | null;
  uptime_seconds: number;
  server_time_utc: string;
  db_status: string;
  celery_status: string;
};

type ModalState =
  | { type: "none" }
  | { type: "invite-user" }
  | { type: "edit-user"; user: UserRead }
  | { type: "add-district" }
  | { type: "edit-district"; district: District }
  | { type: "add-org" }
  | { type: "edit-org"; org: Organization }
  | { type: "confirm-delete-org"; orgId: string }
  | { type: "create-key" }
  | { type: "edit-key"; key: APIKey }
  | { type: "confirm-revoke"; keyId: string }
  | { type: "confirm-suspend"; userId: string }
  | { type: "confirm-delete-district"; districtId: string }
  | { type: "add-case" }
  | { type: "edit-case"; case: MalariaCase }
  | { type: "confirm-delete-case"; caseId: string };

// ── helpers ───────────────────────────────────────────────────────────────────
const initialThresholds: ThresholdConfig = {
  highAlert: 30,
  mediumAlert: 10,
  lowAlert: 0,
};

function mapBackendUser(u: any): UserRead {
  return {
    id: String(u.id),
    name: u.full_name ?? u.name ?? u.email ?? "",
    email: u.email ?? "",
    role: mapRoleLabel(u.role),
    status: u.is_active === false ? "Suspended" : "Active",
    organizationName: u.organization_name ?? u.organizationName ?? "",
    is_active: u.is_active,
    full_name: u.full_name,
  };
}

function mapRoleLabel(role: string): string {
  const map: Record<string, string> = {
    admin: "Admin",
    government_officer: "Government Health Officer",
    org_user: "District Officer",
    viewer: "Viewer",
  };
  return map[role] ?? role;
}

function mapRoleValue(label: string): string {
  const map: Record<string, string> = {
    "Admin": "admin",
    "Government Health Officer": "government_officer",
    "District Officer": "org_user",
    "Viewer": "viewer",
  };
  return map[label] ?? label;
}

function mapBackendDistrict(d: any): District {
  return {
    id: String(d.id),
    name: d.name ?? "",
    region: d.region ?? "",
    population: d.population ?? 0,
    status: "Active",
    lastSync: d.updated_at?.slice(0, 10) ?? d.created_at?.slice(0, 10) ?? "—",
    area_km2: d.area_km2,
  };
}

function mapBackendOrg(o: any): Organization {
  const typeMap: Record<string, Organization["type"]> = {
    government: "Government",
    ngo: "NGO",
    research: "Research",
    international: "NGO",
    private: "Private",
  };
  return {
    id: String(o.id),
    name: o.name ?? "",
    type: typeMap[o.type?.toLowerCase?.()] ?? "Government",
    country: o.country ?? "Cameroon",
    contactEmail: o.contact_email ?? o.contactEmail ?? "",
    userCount: o.user_count ?? 0,
    districtCount: o.district_count ?? 0,
    status: o.is_active === false ? "Suspended" : "Active",
    createdAt: o.created_at?.slice(0, 10) ?? "",
  };
}

function mapBackendCase(c: any): MalariaCase {
  return {
    id: String(c.id),
    district_id: String(c.district_id),
    district_name: c.district_name ?? String(c.district_id),
    week_start: typeof c.week_start === "string" ? c.week_start : String(c.week_start),
    confirmed_cases: c.confirmed_cases ?? 0,
    suspected_cases: c.suspected_cases ?? null,
    deaths: c.deaths ?? null,
    reported_at: c.reported_at ?? new Date().toISOString(),
  };
}

function mapBackendKey(k: any): APIKey {
  return {
    id: String(k.id),
    name: k.name ?? "",
    key: k.key ?? "",
    category: k.category ?? "other",
    provider: k.provider ?? "",
    environment: k.environment ?? "production",
    createdAt: k.created_at ?? "—",
    lastUsed: k.last_used ?? "—",
    status: k.status === "Revoked" ? "Revoked" : "Active",
  };
}

const CATEGORY_META: Record<APIKeyCategory, { label: string; icon: string; color: string; providers: string[] }> = {
  weather:     { label: "Weather",     icon: "ri-cloud-line",        color: "bg-sky-500/10 text-sky-500 border-sky-500/20",         providers: ["Open-Meteo", "OpenWeatherMap", "WeatherAPI", "Tomorrow.io", "Other"] },
  maps:        { label: "Maps",        icon: "ri-map-2-line",        color: "bg-violet-500/10 text-violet-500 border-violet-500/20", providers: ["Mapbox", "Google Maps", "OpenStreetMap", "HERE Maps", "Other"] },
  topography:  { label: "Topography",  icon: "ri-landscape-line",    color: "bg-amber/10 text-amber border-amber/20",               providers: ["Open-Meteo", "OpenTopography", "Google Elevation", "USGS EarthExplorer", "Other"] },
  internal:    { label: "Internal",    icon: "ri-dashboard-line",    color: "bg-teal/10 text-teal border-teal/20",                  providers: ["Internal"] },
  other:       { label: "Other",       icon: "ri-plug-line",         color: "bg-slate-500/10 text-slate-500 border-slate-500/20",   providers: ["Other"] },
};

const ENV_COLOR: Record<string, string> = {
  production:  "bg-emerald/10 text-emerald border-emerald/20",
  staging:     "bg-amber/10 text-amber border-amber/20",
  development: "bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-300 dark:border-slate-700",
};

const ROLE_OPTIONS = ["Admin", "Government Health Officer", "District Officer", "Viewer"];

type Section = "thresholds" | "users" | "districts" | "organizations" | "cases" | "apikeys" | "health";

// ── shared sub-components ─────────────────────────────────────────────────────
function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${color}`}>
      {label}
    </span>
  );
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${active ? "bg-emerald" : "bg-slate-400"}`} />
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{children}</label>;
}

function Input({ className = "", ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal disabled:opacity-50 ${className}`}
      {...props}
    />
  );
}

function Select({ className = "", children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={`w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:border-teal ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

function BtnPrimary({ children, onClick, disabled }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-2 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function BtnGhost({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="px-4 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
      {children}
    </button>
  );
}

function BtnDanger({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600">
      {children}
    </button>
  );
}

function EmptyState({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
        <i className={`${icon} text-slate-400 text-lg`} />
      </div>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{title}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{desc}</p>
    </div>
  );
}

// ── Users section ─────────────────────────────────────────────────────────────
function UsersSection({
  users,
  modal,
  setModal,
  onSave,
  onSuspend,
  showToast,
}: {
  users: UserRead[];
  modal: ModalState;
  setModal: (m: ModalState) => void;
  onSave: (u: UserFormData) => Promise<void>;
  onSuspend: (id: string) => Promise<void>;
  showToast: (msg: string, type?: "success" | "error") => void;
}) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [form, setForm] = useState<UserFormData>({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const list = users.filter((u: UserRead) => {
    const matchSearch =
      search === "" ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "All" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  function openInvite() {
    setForm({ role: "Viewer", status: "Invited", password: "", confirmPassword: "" });
    setShowPassword(false);
    setModal({ type: "invite-user" });
  }

  function openEdit(u: UserRead) {
    setForm({ ...u, password: "", confirmPassword: "" });
    setShowPassword(false);
    setModal({ type: "edit-user", user: u });
  }

  async function handleSave() {
    if (modal.type === "invite-user" && !form.password) {
      showToast("Password is required", "error");
      return;
    }
    if (form.password && form.password !== form.confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    if (form.password && form.password.length < 8) {
      showToast("Password must be at least 8 characters", "error");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      setModal({ type: "none" });
      showToast(modal.type === "invite-user" ? "User created" : "User updated");
    } catch (e: any) {
      showToast(e?.message || "Failed to save user", "error");
    } finally {
      setSaving(false);
    }
  }

  const statusColor: Record<string, string> = {
    Active: "bg-emerald/10 text-emerald border-emerald/20",
    Invited: "bg-amber/10 text-amber border-amber/20",
    Suspended: "bg-red-500/10 text-red-500 border-red-500/20",
  };

  return (
    <>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Users</h3>
            <span className="text-xs text-slate-400">{list.length} shown</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users…"
                className="pl-7 pr-3 py-1.5 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white outline-none focus:border-teal w-44"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="py-1.5 px-2 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 outline-none"
            >
              <option>All</option>
              {ROLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
            </select>
            <button
              onClick={openInvite}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal text-slate-950 text-xs font-semibold hover:bg-teal-hover"
            >
              <i className="ri-user-add-line" /> Invite
            </button>
          </div>
        </div>

        {/* Table */}
        {list.length === 0 ? (
          <EmptyState icon="ri-team-line" title="No users found" desc="Try adjusting your search or role filter." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Organization</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((u: UserRead) => (
                  <tr key={u.id} className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{u.organizationName}</td>
                    <td className="px-4 py-3">
                      <Badge label={u.role} color={getRoleBadgeColor(u.role as any)} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${statusColor[u.status] ?? statusColor.Active}`}>
                        <StatusDot active={u.status === "Active"} />
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(u)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-teal hover:bg-teal/10 transition-colors"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-xs" />
                        </button>
                        <button
                          onClick={() => setModal({ type: "confirm-suspend", userId: u.id })}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-amber hover:bg-amber/10 transition-colors"
                          title={u.status === "Suspended" ? "Reactivate" : "Suspend"}
                        >
                          <i className={u.status === "Suspended" ? "ri-user-follow-line text-xs" : "ri-user-forbid-line text-xs"} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite / Edit modal */}
      {(modal.type === "invite-user" || modal.type === "edit-user") && (
        <Modal
          title={modal.type === "invite-user" ? "Add new user" : "Edit user"}
          onClose={() => setModal({ type: "none" })}
        >
          <div className="space-y-4">
            {modal.type === "invite-user" && (
              <div>
                <FieldLabel>Email address</FieldLabel>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={form.email ?? ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  autoFocus
                />
              </div>
            )}
            <div>
              <FieldLabel>Full name</FieldLabel>
              <Input
                placeholder="Full name"
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel>Role</FieldLabel>
              <Select value={form.role ?? "Viewer"} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
              </Select>
            </div>
            <div>
              <FieldLabel>Organization</FieldLabel>
              <Input
                placeholder="Organization name"
                value={form.organizationName ?? ""}
                onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
              />
            </div>

            {/* Password section */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              {modal.type === "edit-user" && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Leave password fields empty to keep the current password.
                </p>
              )}
              <div className="space-y-3">
                <div>
                  <FieldLabel>{modal.type === "invite-user" ? "Password" : "New password"}</FieldLabel>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={modal.type === "invite-user" ? "Min. 8 characters" : "Leave blank to keep current"}
                      value={form.password ?? ""}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      tabIndex={-1}
                    >
                      <i className={showPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                    </button>
                  </div>
                </div>
                {(form.password ?? "").length > 0 && (
                  <div>
                    <FieldLabel>Confirm password</FieldLabel>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Repeat password"
                      value={form.confirmPassword ?? ""}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    />
                    {form.confirmPassword && form.password !== form.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line" /> Passwords do not match
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
              <BtnPrimary
                onClick={handleSave}
                disabled={
                  saving ||
                  (!form.name && !form.email) ||
                  (!!form.password && form.password !== form.confirmPassword)
                }
              >
                {saving ? "Saving…" : modal.type === "invite-user" ? "Create user" : "Save changes"}
              </BtnPrimary>
            </div>
          </div>
        </Modal>
      )}

      {/* Suspend confirm */}
      {modal.type === "confirm-suspend" && (
        <Modal title="Suspend user?" onClose={() => setModal({ type: "none" })}>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            This will prevent the user from logging in. You can reactivate them at any time.
          </p>
          <div className="flex justify-end gap-2">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnDanger onClick={async () => { await onSuspend(modal.userId); setModal({ type: "none" }); showToast("User status updated"); }}>
              Suspend
            </BtnDanger>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Districts section ─────────────────────────────────────────────────────────
function DistrictsSection({
  modal,
  setModal,
  showToast,
}: {
  modal: ModalState;
  setModal: (m: ModalState) => void;
  showToast: (msg: string, type?: "success" | "error") => void;
}) {
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<Partial<District>>({});
  const [syncing, setSyncing] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.districts()
      .then((data: any[]) => setDistricts(data.map(mapBackendDistrict)))
      .catch(() => {})
      .finally(() => setLoadingDistricts(false));
  }, []);

  const list = districts.filter(
    (d) =>
      search === "" ||
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.region.toLowerCase().includes(search.toLowerCase())
  );

  function openAdd() {
    setForm({ status: "Active" });
    setModal({ type: "add-district" });
  }

  function openEdit(d: District) {
    setForm({ ...d });
    setModal({ type: "edit-district", district: d });
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (modal.type === "add-district") {
        const created = await api.createDistrict({
          name: form.name ?? "",
          region: form.region ?? "",
          population: form.population ?? 0,
          area_km2: form.area_km2,
        });
        setDistricts((prev) => [...prev, mapBackendDistrict(created)]);
        showToast("District added");
      } else if (modal.type === "edit-district") {
        const updated = await api.updateDistrict(form.id!, {
          name: form.name ?? "",
          region: form.region ?? "",
          population: form.population ?? 0,
          area_km2: form.area_km2,
        });
        setDistricts((prev) => prev.map((d) => d.id === form.id ? mapBackendDistrict(updated) : d));
        showToast("District updated");
      }
      setModal({ type: "none" });
    } catch (e: any) {
      showToast(e?.message || "Failed to save district", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.deleteDistrict(id);
      setDistricts((prev) => prev.filter((d) => d.id !== id));
      setModal({ type: "none" });
      showToast("District removed", "error");
    } catch (e: any) {
      showToast(e?.message || "Failed to remove district", "error");
    }
  }

  async function handleSync(id: string) {
    setSyncing(id);
    try {
      await api.syncDistrictTopography(id);
      setDistricts((prev) =>
        prev.map((d) => (d.id === id ? { ...d, lastSync: new Date().toISOString().slice(0, 10) } : d))
      );
      showToast("Topography sync triggered");
    } catch {
      showToast("Sync triggered (runs in background)");
    } finally {
      setSyncing(null);
    }
  }

  if (loadingDistricts) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-700 border-t-teal rounded-full animate-spin" /></div>;

  return (
    <>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Districts</h3>
            <span className="text-xs text-slate-400">{list.length} total</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search districts…"
                className="pl-7 pr-3 py-1.5 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white outline-none focus:border-teal w-44"
              />
            </div>
            <button
              onClick={openAdd}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal text-slate-950 text-xs font-semibold hover:bg-teal-hover"
            >
              <i className="ri-map-pin-add-line" /> Add district
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <EmptyState icon="ri-map-pin-line" title="No districts found" desc="Add a district or adjust your search." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">District</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Region</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Population</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Last topo sync</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id} className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">{d.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{d.region}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{d.population.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${d.status === "Active" ? "bg-emerald/10 text-emerald border-emerald/20" : "bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700"}`}>
                        <StatusDot active={d.status === "Active"} />
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">{d.lastSync}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSync(d.id)}
                          disabled={syncing === d.id}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-teal hover:bg-teal/10 transition-colors disabled:opacity-40"
                          title="Sync topography"
                        >
                          <i className={`text-xs ${syncing === d.id ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}`} />
                        </button>
                        <button
                          onClick={() => openEdit(d)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-teal hover:bg-teal/10 transition-colors"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-xs" />
                        </button>
                        <button
                          onClick={() => setModal({ type: "confirm-delete-district", districtId: d.id })}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                          title="Remove"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal.type === "add-district" || modal.type === "edit-district") && (
        <Modal
          title={modal.type === "add-district" ? "Add district" : "Edit district"}
          onClose={() => setModal({ type: "none" })}
        >
          <div className="space-y-4">
            <div>
              <FieldLabel>District name</FieldLabel>
              <Input
                placeholder="e.g. Yaoundé Centre"
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel>Region</FieldLabel>
              <Input
                placeholder="e.g. Centre"
                value={form.region ?? ""}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel>Population</FieldLabel>
              <Input
                type="number"
                placeholder="0"
                value={form.population ?? ""}
                onChange={(e) => setForm({ ...form, population: Number(e.target.value) })}
              />
            </div>
            <div>
              <FieldLabel>Status</FieldLabel>
              <Select
                value={form.status ?? "Active"}
                onChange={(e) => setForm({ ...form, status: e.target.value as District["status"] })}
              >
                <option>Active</option>
                <option>Inactive</option>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
              <BtnPrimary onClick={handleSave} disabled={saving || !form.name}>
                {saving ? "Saving…" : modal.type === "add-district" ? "Add district" : "Save changes"}
              </BtnPrimary>
            </div>
          </div>
        </Modal>
      )}

      {modal.type === "confirm-delete-district" && (
        <Modal title="Remove district?" onClose={() => setModal({ type: "none" })}>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            This will remove the district and all its topography features. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnDanger onClick={() => handleDelete(modal.districtId)}>Remove district</BtnDanger>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Organizations section ─────────────────────────────────────────────────────
function OrganizationsSection({
  modal,
  setModal,
  showToast,
}: {
  modal: ModalState;
  setModal: (m: ModalState) => void;
  showToast: (msg: string, type?: "success" | "error") => void;
}) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [form, setForm] = useState<Partial<Organization>>({});

  useEffect(() => {
    api.organizations()
      .then((data: any[]) => setOrgs(data.map(mapBackendOrg)))
      .catch(() => {})
      .finally(() => setLoadingOrgs(false));
  }, []);

  const ORG_TYPES = ["Government", "NGO", "Research", "Private"] as const;

  const list = orgs.filter((o) => {
    const matchSearch = search === "" || o.name.toLowerCase().includes(search.toLowerCase()) || o.country.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "All" || o.type === typeFilter;
    return matchSearch && matchType;
  });

  const TYPE_COLOR: Record<string, string> = {
    Government: "bg-violet-500/10 text-violet-500 border-violet-500/20",
    NGO:        "bg-sky-500/10 text-sky-500 border-sky-500/20",
    Research:   "bg-amber/10 text-amber border-amber/20",
    Private:    "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  };

  function openAdd() {
    setForm({ type: "Government", status: "Active", country: "Cameroon" });
    setModal({ type: "add-org" });
  }

  function openEdit(o: Organization) {
    setForm({ ...o });
    setModal({ type: "edit-org", org: o });
  }

  async function handleSave() {
    setSaving(true);
    const typeApiMap: Record<string, string> = { Government: "government", NGO: "ngo", Research: "research", Private: "ngo" };
    try {
      if (modal.type === "add-org") {
        const created = await api.createOrganization({ name: form.name ?? "", type: typeApiMap[form.type ?? "Government"] });
        setOrgs((prev) => [...prev, mapBackendOrg(created)]);
        showToast("Organization added");
      } else if (modal.type === "edit-org") {
        const updated = await api.updateOrganization(form.id!, { name: form.name ?? "", type: typeApiMap[form.type ?? "Government"] });
        setOrgs((prev) => prev.map((o) => o.id === form.id ? mapBackendOrg(updated) : o));
        showToast("Organization updated");
      }
      setModal({ type: "none" });
    } catch (e: any) {
      showToast(e?.message || "Failed to save organization", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.deleteOrganization(id);
      setOrgs((prev) => prev.filter((o) => o.id !== id));
      setModal({ type: "none" });
      showToast("Organization removed", "error");
    } catch (e: any) {
      showToast(e?.message || "Failed to remove organization", "error");
    }
  }

  function toggleStatus(id: string) {
    setOrgs((prev) => prev.map((o) => o.id === id ? { ...o, status: o.status === "Active" ? "Suspended" : "Active" } : o));
    showToast("Organization status updated");
  }

  if (loadingOrgs) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-700 border-t-teal rounded-full animate-spin" /></div>;

  return (
    <>
      <div className="space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(["Government", "NGO", "Research", "Private"] as const).map((t) => {
            const count = orgs.filter((o) => o.type === t && o.status === "Active").length;
            return (
              <div key={t} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{t}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{count}</p>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border mt-1 ${TYPE_COLOR[t]}`}>{t}</span>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Organizations</h3>
              <span className="text-xs text-slate-400">{list.length} shown</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search organizations…"
                  className="pl-7 pr-3 py-1.5 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white outline-none focus:border-teal w-44"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="py-1.5 px-2 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 outline-none"
              >
                <option>All</option>
                {ORG_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <button
                onClick={openAdd}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal text-slate-950 text-xs font-semibold hover:bg-teal-hover"
              >
                <i className="ri-add-line" /> Add org
              </button>
            </div>
          </div>

          {list.length === 0 ? (
            <EmptyState icon="ri-building-2-line" title="No organizations found" desc="Add an organization or adjust your filters." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Organization</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Country</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Users</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Districts</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((o) => (
                    <tr key={o.id} className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{o.name}</p>
                        <p className="text-xs text-slate-500">{o.contactEmail}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${TYPE_COLOR[o.type]}`}>{o.type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{o.country}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{o.userCount}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{o.districtCount}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${o.status === "Active" ? "bg-emerald/10 text-emerald border-emerald/20" : "bg-red-500/10 text-red-500 border-red-500/20"}`}>
                          <StatusDot active={o.status === "Active"} />{o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(o)} className="p-1.5 rounded-lg text-slate-400 hover:text-teal hover:bg-teal/10 transition-colors" title="Edit">
                            <i className="ri-pencil-line text-xs" />
                          </button>
                          <button onClick={() => toggleStatus(o.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-amber hover:bg-amber/10 transition-colors" title={o.status === "Active" ? "Suspend" : "Reactivate"}>
                            <i className={`text-xs ${o.status === "Active" ? "ri-pause-circle-line" : "ri-play-circle-line"}`} />
                          </button>
                          <button onClick={() => setModal({ type: "confirm-delete-org", orgId: o.id })} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-colors" title="Remove">
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit modal */}
      {(modal.type === "add-org" || modal.type === "edit-org") && (
        <Modal title={modal.type === "add-org" ? "Add organization" : "Edit organization"} onClose={() => setModal({ type: "none" })}>
          <div className="space-y-4">
            <div>
              <FieldLabel>Organization name</FieldLabel>
              <Input placeholder="e.g. Ministry of Health" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Type</FieldLabel>
                <Select value={form.type ?? "Government"} onChange={(e) => setForm({ ...form, type: e.target.value as Organization["type"] })}>
                  {ORG_TYPES.map((t) => <option key={t}>{t}</option>)}
                </Select>
              </div>
              <div>
                <FieldLabel>Country</FieldLabel>
                <Input placeholder="e.g. Cameroon" value={form.country ?? ""} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
            </div>
            <div>
              <FieldLabel>Contact email</FieldLabel>
              <Input type="email" placeholder="contact@org.org" value={form.contactEmail ?? ""} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
            <div>
              <FieldLabel>Status</FieldLabel>
              <Select value={form.status ?? "Active"} onChange={(e) => setForm({ ...form, status: e.target.value as Organization["status"] })}>
                <option>Active</option>
                <option>Suspended</option>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
              <BtnPrimary onClick={handleSave} disabled={saving || !form.name}>{saving ? "Saving…" : modal.type === "add-org" ? "Add organization" : "Save changes"}</BtnPrimary>
            </div>
          </div>
        </Modal>
      )}

      {modal.type === "confirm-delete-org" && (
        <Modal title="Remove organization?" onClose={() => setModal({ type: "none" })}>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            This will permanently remove the organization and unlink all associated users. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnDanger onClick={() => handleDelete(modal.orgId)}>Remove</BtnDanger>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── API Keys section ──────────────────────────────────────────────────────────
function ApiKeysSection({
  modal,
  setModal,
  showToast,
}: {
  modal: ModalState;
  setModal: (m: ModalState) => void;
  showToast: (msg: string, type?: "success" | "error") => void;
}) {
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);

  useEffect(() => {
    api.settingsApiKeys()
      .then((data: any[]) => setKeys(data.map(mapBackendKey)))
      .catch(() => {})
      .finally(() => setLoadingKeys(false));
  }, []);

  const [visibleKey, setVisibleKey] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<APIKeyCategory | "all">("all");
  const [justCreated, setJustCreated] = useState<APIKey | null>(null);
  const [form, setForm] = useState<Partial<APIKey>>({});

  const CATEGORIES = Object.keys(CATEGORY_META) as APIKeyCategory[];

  const visibleKeys = categoryFilter === "all"
    ? keys
    : keys.filter((k) => k.category === categoryFilter);

  function maskKey(k: string) {
    if (k.startsWith("om_free")) return k; // Open-Meteo needs no key
    return k.slice(0, 8) + "•".repeat(14) + k.slice(-4);
  }

  function isFreeKey(k: APIKey) {
    return k.key.startsWith("om_free");
  }

  function openCreate() {
    setForm({ category: "weather", environment: "production", provider: "Open-Meteo" });
    setModal({ type: "create-key" });
  }

  function openEdit(k: APIKey) {
    setForm({ ...k });
    setModal({ type: "edit-key", key: k });
  }

  function handleSave() {
    if (modal.type === "create-key") {
      const newKey: APIKey = {
        id: `k${Date.now()}`,
        name: form.name ?? "",
        key: form.key ?? "",
        category: form.category ?? "other",
        provider: form.provider ?? "",
        environment: form.environment ?? "production",
        createdAt: new Date().toISOString().slice(0, 10),
        lastUsed: "—",
        status: "Active",
      };
      setKeys((prev) => [newKey, ...prev]);
      if (!isFreeKey(newKey)) setJustCreated(newKey);
      showToast("API key saved");
    } else if (modal.type === "edit-key") {
      setKeys((prev) => prev.map((k) => k.id === form.id ? { ...k, ...form } as APIKey : k));
      showToast("API key updated");
    }
    setModal({ type: "none" });
  }

  function handleRevoke(id: string) {
    setKeys((prev) => prev.map((k) => k.id === id ? { ...k, status: "Revoked" } : k));
    setModal({ type: "none" });
    showToast("Key revoked", "error");
  }

  function handleRegenerate(id: string) {
    const raw = `mk_live_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;
    setKeys((prev) => prev.map((k) => k.id === id ? { ...k, key: raw, status: "Active", lastUsed: "—" } : k));
    showToast("Key regenerated — copy it now");
  }

  function copy(text: string) {
    navigator.clipboard.writeText(text).then(() => showToast("Copied to clipboard"));
  }

  // When category changes in form, default provider to first option
  function setCategory(cat: APIKeyCategory) {
    const providers = CATEGORY_META[cat].providers;
    setForm((f) => ({ ...f, category: cat, provider: providers[0] }));
  }

  if (loadingKeys) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-700 border-t-teal rounded-full animate-spin" /></div>;

  const KeyForm = () => (
    <div className="space-y-4">
      {/* Category picker */}
      <div>
        <FieldLabel>Category</FieldLabel>
        <div className="grid grid-cols-3 gap-2">
          {CATEGORIES.map((cat) => {
            const meta = CATEGORY_META[cat];
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setCategory(cat)}
                className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border text-xs font-medium transition-all ${form.category === cat ? `${meta.color} border-current` : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300 dark:hover:border-slate-600"}`}
              >
                <i className={`${meta.icon} text-base`} />
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Provider</FieldLabel>
          <Select
            value={form.provider ?? ""}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
          >
            {(CATEGORY_META[form.category ?? "other"]?.providers ?? ["Other"]).map((p) => (
              <option key={p}>{p}</option>
            ))}
          </Select>
        </div>
        <div>
          <FieldLabel>Environment</FieldLabel>
          <Select
            value={form.environment ?? "production"}
            onChange={(e) => setForm({ ...form, environment: e.target.value as APIKey["environment"] })}
          >
            <option value="production">Production</option>
            <option value="staging">Staging</option>
            <option value="development">Development</option>
          </Select>
        </div>
      </div>

      <div>
        <FieldLabel>Label</FieldLabel>
        <Input
          placeholder={`e.g. ${CATEGORY_META[form.category ?? "other"]?.label} – Production`}
          value={form.name ?? ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>

      <div>
        <FieldLabel>API key / token</FieldLabel>
        <Input
          type="text"
          placeholder={form.provider === "Open-Meteo" ? "No key required — leave blank" : "Paste your key here"}
          value={form.key ?? ""}
          onChange={(e) => setForm({ ...form, key: e.target.value })}
        />
        {form.provider === "Open-Meteo" && (
          <p className="text-xs text-emerald mt-1 flex items-center gap-1">
            <i className="ri-check-circle-line" /> Open-Meteo is free with no API key required.
          </p>
        )}
        {form.provider === "OpenTopography" && (
          <p className="text-xs text-slate-400 mt-1">
            Free key available at <span className="text-teal">opentopography.org/developers</span> — 200 calls/day for academics.
          </p>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-4">
        {/* Just-created banner */}
        {justCreated && (
          <div className="rounded-xl border border-teal/30 bg-teal/5 p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-teal mb-1">Key saved — copy it now. You won't see it again.</p>
              <code className="text-xs text-slate-700 dark:text-slate-300 break-all">{justCreated.key}</code>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => copy(justCreated.key)} className="p-1.5 rounded-lg text-teal hover:bg-teal/10"><i className="ri-file-copy-line text-xs" /></button>
              <button onClick={() => setJustCreated(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600"><i className="ri-close-line text-xs" /></button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          {/* Header + category filter tabs */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">API Keys</h3>
                <span className="text-xs text-slate-400">{keys.filter((k) => k.status === "Active").length} active</span>
              </div>
              <button
                onClick={openCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal text-slate-950 text-xs font-semibold hover:bg-teal-hover"
              >
                <i className="ri-add-line" /> Add key
              </button>
            </div>
            {/* Category filter pills */}
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setCategoryFilter("all")}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${categoryFilter === "all" ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300"}`}
              >
                All
              </button>
              {CATEGORIES.map((cat) => {
                const meta = CATEGORY_META[cat];
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${categoryFilter === cat ? `${meta.color}` : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300"}`}
                  >
                    <i className={`${meta.icon} text-[10px]`} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {visibleKeys.length === 0 ? (
            <EmptyState icon="ri-key-line" title="No keys in this category" desc="Add a key to connect this service." />
          ) : (
            <div className="divide-y divide-slate-200/50 dark:divide-slate-800/60">
              {visibleKeys.map((k) => {
                const meta = CATEGORY_META[k.category];
                const free = isFreeKey(k);
                return (
                  <div key={k.id} className={`px-4 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${k.status === "Revoked" ? "opacity-50" : ""}`}>
                    <div className="flex items-start gap-3 min-w-0">
                      {/* Category icon */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${meta.color}`}>
                        <i className={`${meta.icon} text-sm`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{k.name}</p>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${ENV_COLOR[k.environment]}`}>{k.environment}</span>
                          {k.status === "Revoked" && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-red-500/10 text-red-500 border-red-500/20">Revoked</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-400">{k.provider}</span>
                          <span className="text-slate-300 dark:text-slate-600">·</span>
                          {free ? (
                            <span className="text-[10px] text-emerald flex items-center gap-0.5"><i className="ri-check-circle-line" /> No key required</span>
                          ) : (
                            <>
                              <code className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                                {visibleKey === k.id ? k.key : maskKey(k.key)}
                              </code>
                              <button onClick={() => setVisibleKey(visibleKey === k.id ? null : k.id)} className="text-slate-400 hover:text-slate-600" disabled={k.status === "Revoked"}>
                                <i className={`text-xs ${visibleKey === k.id ? "ri-eye-off-line" : "ri-eye-line"}`} />
                              </button>
                              <button onClick={() => copy(k.key)} className="text-slate-400 hover:text-teal" disabled={k.status === "Revoked"}>
                                <i className="ri-file-copy-line text-xs" />
                              </button>
                            </>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">Added {k.createdAt} · Last used {k.lastUsed}</p>
                      </div>
                    </div>

                    {k.status === "Active" && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openEdit(k)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-teal hover:text-teal transition-colors"
                        >
                          <i className="ri-pencil-line" /> Edit
                        </button>
                        {!free && (
                          <button
                            onClick={() => handleRegenerate(k.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-teal hover:text-teal transition-colors"
                          >
                            <i className="ri-refresh-line" /> Rotate
                          </button>
                        )}
                        <button
                          onClick={() => setModal({ type: "confirm-revoke", keyId: k.id })}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-200 dark:border-red-500/20 text-red-500 hover:bg-red-500/10 transition-colors"
                        >
                          <i className="ri-forbid-line" /> Revoke
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info footer */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 flex items-start gap-3">
          <i className="ri-information-line text-slate-400 mt-0.5" />
          <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
            <p>Topography keys (OpenTopography) and weather keys (Open-Meteo) are used by your Celery sync tasks — changes take effect on the next scheduled run.</p>
            <p>Never expose keys in client-side code. Revoking a key is immediate and cannot be undone.</p>
          </div>
        </div>
      </div>

      {/* Create / Edit modal */}
      {(modal.type === "create-key" || modal.type === "edit-key") && (
        <Modal
          title={modal.type === "create-key" ? "Add API key" : "Edit API key"}
          onClose={() => setModal({ type: "none" })}
        >
          <KeyForm />
          <div className="flex justify-end gap-2 pt-4">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnPrimary onClick={handleSave} disabled={!form.name || (!form.key && form.provider !== "Open-Meteo")}>
              {modal.type === "create-key" ? "Save key" : "Update key"}
            </BtnPrimary>
          </div>
        </Modal>
      )}

      {/* Revoke confirm */}
      {modal.type === "confirm-revoke" && (
        <Modal title="Revoke key?" onClose={() => setModal({ type: "none" })}>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            Any service using this key will immediately lose access. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnDanger onClick={() => handleRevoke(modal.keyId)}>Revoke key</BtnDanger>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Cases section ─────────────────────────────────────────────────────────────
function CasesSection({
  modal,
  setModal,
  showToast,
}: {
  modal: ModalState;
  setModal: (m: ModalState) => void;
  showToast: (msg: string, type?: "success" | "error") => void;
}) {
  const [cases, setCases] = useState<MalariaCase[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [savingCase, setSavingCase] = useState(false);
  const [form, setForm] = useState<Partial<MalariaCase>>({});
  const [search, setSearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState("All");
  const [weekFilter, setWeekFilter] = useState("");

  useEffect(() => {
    Promise.all([api.malariaCases({ per_page: 200 }), api.districts()])
      .then(([casesRes, distRes]: [any, any[]]) => {
        setCases((casesRes?.items ?? []).map(mapBackendCase));
        setDistricts(distRes.map(mapBackendDistrict));
      })
      .catch(() => {})
      .finally(() => setLoadingCases(false));
  }, []);

  // Derive unique districts and weeks
  const districtOptions = districts.map((d) => d.name);
  const weekOptions = Array.from(new Set(cases.map((c) => c.week_start))).sort().reverse();

  const filtered = cases.filter((c) => {
    const matchSearch =
      search === "" ||
      c.district_name.toLowerCase().includes(search.toLowerCase());
    const matchDistrict = districtFilter === "All" || c.district_name === districtFilter;
    const matchWeek = weekFilter === "" || c.week_start === weekFilter;
    return matchSearch && matchDistrict && matchWeek;
  });

  // Summary totals over filtered set
  const totalConfirmed = filtered.reduce((s, c) => s + c.confirmed_cases, 0);
  const totalSuspected = filtered.reduce((s, c) => s + (c.suspected_cases ?? 0), 0);
  const totalDeaths = filtered.reduce((s, c) => s + (c.deaths ?? 0), 0);
  const cfr = totalConfirmed > 0 ? ((totalDeaths / totalConfirmed) * 100).toFixed(1) : "0.0";

  function openAdd() {
    const monday = getMondayOfCurrentWeek();
    setForm({
      confirmed_cases: 0,
      suspected_cases: null,
      deaths: 0,
      week_start: monday,
      district_id: districts[0]?.id ?? "",
      district_name: districts[0]?.name ?? "",
    });
    setModal({ type: "add-case" });
  }

  function openEdit(c: MalariaCase) {
    setForm({ ...c });
    setModal({ type: "edit-case", case: c });
  }

  async function handleSave() {
    setSavingCase(true);
    try {
      if (modal.type === "add-case") {
        const created = await api.createMalariaCase({
          district_id: Number(form.district_id),
          week_start: form.week_start ?? getMondayOfCurrentWeek(),
          confirmed_cases: form.confirmed_cases ?? 0,
          suspected_cases: form.suspected_cases ?? null,
          deaths: form.deaths ?? 0,
        });
        setCases((prev) => [mapBackendCase(created), ...prev]);
        showToast("Case report added");
      } else if (modal.type === "edit-case") {
        const updated = await api.updateMalariaCase(form.id!, {
          district_id: Number(form.district_id),
          week_start: form.week_start ?? getMondayOfCurrentWeek(),
          confirmed_cases: form.confirmed_cases ?? 0,
          suspected_cases: form.suspected_cases ?? null,
          deaths: form.deaths ?? 0,
        });
        setCases((prev) => prev.map((c) => c.id === form.id ? mapBackendCase(updated) : c));
        showToast("Case report updated");
      }
      setModal({ type: "none" });
    } catch (e: any) {
      showToast(e?.message || "Failed to save case report", "error");
    } finally {
      setSavingCase(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.deleteMalariaCase(id);
      setCases((prev) => prev.filter((c) => c.id !== id));
      setModal({ type: "none" });
      showToast("Case report deleted", "error");
    } catch (e: any) {
      showToast(e?.message || "Failed to delete case report", "error");
    }
  }

  function onDistrictChange(name: string) {
    const d = districts.find((d) => d.name === name);
    setForm((f) => ({ ...f, district_name: name, district_id: d?.id ?? "" }));
  }

  function getMondayOfCurrentWeek(): string {
    const now = new Date();
    const day = now.getDay(); // 0=Sun
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    return monday.toISOString().slice(0, 10);
  }

  function severityColor(confirmed: number): string {
    if (confirmed >= 60) return "text-red-500";
    if (confirmed >= 30) return "text-amber";
    return "text-emerald";
  }

  function severityBadge(confirmed: number): string {
    if (confirmed >= 60) return "bg-red-500/10 text-red-500 border-red-500/20";
    if (confirmed >= 30) return "bg-amber/10 text-amber border-amber/20";
    return "bg-emerald/10 text-emerald border-emerald/20";
  }

  if (loadingCases) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-700 border-t-teal rounded-full animate-spin" /></div>;

  const CaseForm = () => (
    <div className="space-y-4">
      {/* District */}
      <div>
        <FieldLabel>District</FieldLabel>
        <Select
          value={form.district_name ?? ""}
          onChange={(e) => onDistrictChange(e.target.value)}
        >
          {districts.map((d) => (
            <option key={d.id} value={d.name}>{d.name}</option>
          ))}
        </Select>
      </div>

      {/* Week start */}
      <div>
        <FieldLabel>Week start (Monday)</FieldLabel>
        <Input
          type="date"
          value={form.week_start ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, week_start: e.target.value }))}
        />
        <p className="text-xs text-slate-400 mt-1">Use the Monday of the epidemiological week.</p>
      </div>

      {/* Case counts */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <FieldLabel>Confirmed cases</FieldLabel>
          <Input
            type="number"
            min={0}
            value={form.confirmed_cases ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, confirmed_cases: Math.max(0, Number(e.target.value)) }))}
          />
        </div>
        <div>
          <FieldLabel>Suspected cases</FieldLabel>
          <Input
            type="number"
            min={0}
            placeholder="optional"
            value={form.suspected_cases ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, suspected_cases: e.target.value === "" ? null : Math.max(0, Number(e.target.value)) }))}
          />
        </div>
        <div>
          <FieldLabel>Deaths</FieldLabel>
          <Input
            type="number"
            min={0}
            value={form.deaths ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, deaths: Math.max(0, Number(e.target.value)) }))}
          />
        </div>
      </div>

      {/* Inline validation hint */}
      {(form.deaths ?? 0) > (form.confirmed_cases ?? 0) && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line" /> Deaths cannot exceed confirmed cases.
        </p>
      )}
      {(form.suspected_cases ?? 0) > 0 && (form.suspected_cases ?? 0) < (form.confirmed_cases ?? 0) && (
        <p className="text-xs text-amber flex items-center gap-1">
          <i className="ri-alert-line" /> Suspected cases is less than confirmed — double-check.
        </p>
      )}
    </div>
  );

  return (
    <>
      <div className="space-y-4">
        {/* Summary strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Confirmed", value: totalConfirmed.toLocaleString(), icon: "ri-virus-line", color: "text-red-500" },
            { label: "Suspected", value: totalSuspected.toLocaleString(), icon: "ri-microscope-line", color: "text-amber" },
            { label: "Deaths", value: totalDeaths.toLocaleString(), icon: "ri-heart-pulse-line", color: "text-slate-500" },
            { label: "Case fatality", value: `${cfr}%`, icon: "ri-percent-line", color: "text-violet-500" },
          ].map(({ label, value, icon, color }) => (
            <div key={label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
              <div className="flex items-center gap-2 mb-1">
                <i className={`${icon} ${color} text-sm`} />
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Table card */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Case Reports</h3>
              <span className="text-xs text-slate-400">{filtered.length} records</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative">
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search district…"
                  className="pl-7 pr-3 py-1.5 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white outline-none focus:border-teal w-36"
                />
              </div>
              {/* District filter */}
              <select
                value={districtFilter}
                onChange={(e) => setDistrictFilter(e.target.value)}
                className="py-1.5 px-2 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 outline-none"
              >
                <option value="All">All districts</option>
                {districtOptions.map((d) => <option key={d}>{d}</option>)}
              </select>
              {/* Week filter */}
              <select
                value={weekFilter}
                onChange={(e) => setWeekFilter(e.target.value)}
                className="py-1.5 px-2 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 outline-none"
              >
                <option value="">All weeks</option>
                {weekOptions.map((w) => <option key={w} value={w}>Week of {w}</option>)}
              </select>
              <button
                onClick={openAdd}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal text-slate-950 text-xs font-semibold hover:bg-teal-hover"
              >
                <i className="ri-add-line" /> Add report
              </button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-file-list-3-line"
              title="No case reports"
              desc="Add a weekly report or adjust your filters."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">District</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Week of</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Confirmed</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Suspected</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Deaths</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">CFR</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Reported</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const caseCfr =
                      c.confirmed_cases > 0
                        ? ((( c.deaths ?? 0) / c.confirmed_cases) * 100).toFixed(1)
                        : "0.0";
                    return (
                      <tr
                        key={c.id}
                        className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                          {c.district_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {c.week_start}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${severityBadge(c.confirmed_cases)}`}>
                            {c.confirmed_cases}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {c.suspected_cases ?? <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                          {c.deaths ?? 0}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                          {caseCfr}%
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {new Date(c.reported_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEdit(c)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-teal hover:bg-teal/10 transition-colors"
                              title="Edit"
                            >
                              <i className="ri-pencil-line text-xs" />
                            </button>
                            <button
                              onClick={() => setModal({ type: "confirm-delete-case", caseId: c.id })}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info footer */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 flex items-start gap-3">
          <i className="ri-information-line text-slate-400 mt-0.5" />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Reports are aggregated weekly by district. <span className="text-teal font-medium">Week start must always be a Monday</span> — this aligns with WHO epidemiological week convention and is the key your XGBoost model uses to join climate and topography features.
          </p>
        </div>
      </div>

      {/* Add / Edit modal */}
      {(modal.type === "add-case" || modal.type === "edit-case") && (
        <Modal
          title={modal.type === "add-case" ? "Add case report" : "Edit case report"}
          onClose={() => setModal({ type: "none" })}
        >
          <CaseForm />
          <div className="flex justify-end gap-2 pt-4">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnPrimary
              onClick={handleSave}
              disabled={
                savingCase ||
                !form.district_id ||
                !form.week_start ||
                (form.deaths ?? 0) > (form.confirmed_cases ?? 0)
              }
            >
              {savingCase ? "Saving…" : modal.type === "add-case" ? "Add report" : "Save changes"}
            </BtnPrimary>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {modal.type === "confirm-delete-case" && (
        <Modal title="Delete case report?" onClose={() => setModal({ type: "none" })}>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            This will permanently remove this weekly report. Your model's training data will be affected if it has already been used for prediction.
          </p>
          <div className="flex justify-end gap-2">
            <BtnGhost onClick={() => setModal({ type: "none" })}>Cancel</BtnGhost>
            <BtnDanger onClick={() => handleDelete(modal.caseId)}>Delete</BtnDanger>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Health section ───────────────────────────────────────────────────────────
function HealthSection({ showToast }: { showToast: (msg: string, type?: "success" | "error") => void }) {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  function refresh() {
    setLoading(true);
    api.settingsHealth()
      .then((data: any) => setHealth(data))
      .catch(() => showToast("Failed to load health data", "error"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { refresh(); }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-700 border-t-teal rounded-full animate-spin" /></div>;
  if (!health) return <p className="text-sm text-slate-500 py-6 text-center">Health data unavailable.</p>;

  const svcs = [
    { name: "API Server",         status: health.api_status,    icon: "ri-server-line",        detail: `Uptime: ${Math.floor(health.uptime_seconds / 3600)}h ${Math.floor((health.uptime_seconds % 3600) / 60)}m` },
    { name: "Database",           status: health.db_status,     icon: "ri-database-2-line",    detail: "PostgreSQL" },
    { name: "Task Queue",         status: health.celery_status, icon: "ri-cpu-line",           detail: "Celery + Redis" },
    { name: "ML Model (2w)",      status: health.model_version_2w ? "Loaded" : "Not trained", icon: "ri-brain-line", detail: health.model_version_2w ?? "no model" },
    { name: "ML Model (4w)",      status: health.model_version_4w ? "Loaded" : "Not trained", icon: "ri-brain-line", detail: health.model_version_4w ?? "no model" },
    { name: "ML Model (12w)",     status: health.model_version_12w ? "Loaded" : "Not trained", icon: "ri-brain-line", detail: health.model_version_12w ?? "no model" },
  ];

  function svcColor(status: string) {
    const s = status?.toLowerCase();
    if (s === "ok" || s === "operational" || s === "loaded") return "text-emerald";
    if (s === "degraded") return "text-amber";
    return "text-red-500";
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">System Health</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">{new Date(health.server_time_utc).toLocaleString()}</span>
          <button onClick={refresh} className="flex items-center gap-1.5 text-xs text-teal hover:opacity-75 transition">
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {svcs.map((svc) => (
          <div key={svc.name} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className={`${svc.icon} text-slate-400 text-sm`} />
              <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">{svc.name}</p>
            </div>
            <p className={`text-sm font-semibold capitalize ${svcColor(svc.status)}`}>{svc.status}</p>
            <p className="text-[10px] text-slate-400 mt-1 truncate">{svc.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Settings page ────────────────────────────────────────────────────────
export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const displayUser = user ?? ({} as any);
  const role = displayUser?.role as any;

  const canManage = role === "Government Health Officer" || role === "Admin";
  const canThresholds = canManage;
  const canApiKeys = role === "Admin";
  const isViewOnly = role === "Viewer";

  const [activeSection, setActiveSection] = useState<Section>("thresholds");
  const [loading, setLoading] = useState(false);
  const [thresholds, setThresholds] = useState<ThresholdConfig>(initialThresholds);
  const [users, setUsers] = useState<UserRead[]>([]);
  const [modal, setModal] = useState<ModalState>({ type: "none" });
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const sections = useMemo(() => {
    const all = [
      { id: "thresholds" as const, label: t("settings_thresholds"), icon: "ri-settings-4-line", visible: canThresholds || isViewOnly },
      { id: "users" as const, label: t("settings_users"), icon: "ri-team-line", visible: canManage },
      { id: "districts" as const, label: "Districts", icon: "ri-map-pin-line", visible: canManage },
      { id: "organizations" as const, label: "Organizations", icon: "ri-building-2-line", visible: canManage },
      { id: "cases" as const, label: "Case Reports", icon: "ri-file-list-3-line", visible: canManage },
      { id: "apikeys" as const, label: t("settings_apikeys"), icon: "ri-key-line", visible: canApiKeys },
      { id: "health" as const, label: t("settings_health"), icon: "ri-heart-pulse-line", visible: true },
    ];
    return all.filter((s) => s.visible);
  }, [canApiKeys, canManage, canThresholds, isViewOnly, t]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [thresholdsData, usersData] = await Promise.all([
          api.settingsThresholds(),
          api.settingsUsers(),
        ]);
        if (cancelled) return;
        setThresholds({
          highAlert: thresholdsData?.high_alert_threshold_pct ?? initialThresholds.highAlert,
          mediumAlert: thresholdsData?.medium_alert_threshold_pct ?? initialThresholds.mediumAlert,
          lowAlert: thresholdsData?.low_alert_threshold_pct ?? initialThresholds.lowAlert,
        });
        setUsers((usersData ?? []).map(mapBackendUser));
      } catch (e: any) {
        showToast(e?.message || "Failed to load settings", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function showToast(message: string, type: "success" | "error" = "success") {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 2500);
  }

  async function saveThresholds() {
    try {
      await api.patchSettingsThresholds({
        alert_high_threshold_pct: thresholds.highAlert,
        alert_medium_threshold_pct: thresholds.mediumAlert,
      });
      showToast(t("common_success"));
    } catch {
      showToast(t("common_error"), "error");
    }
  }

  async function handleSaveUser(data: UserFormData) {
    if (data.id) {
      const patch: Record<string, unknown> = {
        full_name: data.name,
        role: mapRoleValue(data.role ?? "Viewer"),
        is_active: data.status !== "Suspended",
      };
      if (data.password) patch.password = data.password;
      const updated = await api.patchSettingsUser(data.id, patch);
      setUsers((prev) => prev.map((u) => u.id === data.id ? mapBackendUser(updated) : u));
    } else {
      const created = await api.createSettingsUser({
        email: data.email ?? "",
        full_name: data.name ?? "",
        role: mapRoleValue(data.role ?? "Viewer"),
        password: data.password ?? "",
      });
      setUsers((prev) => [...prev, mapBackendUser(created)]);
    }
  }

  async function handleSuspendUser(id: string) {
    const u = users.find((u) => u.id === id);
    const updated = await api.patchSettingsUser(id, { is_active: u?.status === "Suspended" });
    setUsers((prev) => prev.map((u) => u.id === id ? mapBackendUser(updated) : u));
  }

  return (
    <div className="animate-fade-in space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("settings_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("settings_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{t("settings_your_role")}</span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor((role ?? "Viewer") as any)}`}>
            {role}
          </span>
        </div>
      </div>

      {/* Nav tabs */}
      <div className="flex flex-wrap gap-1 p-1 bg-slate-100 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-800 w-fit">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeSection === section.id
                ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
          >
            <i className={`${section.icon} text-xs`} />
            {section.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-6">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
          </div>
        </div>
      )}

      {!loading && (
        <>
          {/* ── Thresholds ── */}
          {activeSection === "thresholds" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">{t("settings_threshold_config_title")}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t("settings_threshold_desc")}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-red-300 dark:border-red-500/20 bg-red-50 dark:bg-red-500/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-red-500/20">
                      <i className="ri-error-warning-line text-red-500 dark:text-red-400 text-xs" />
                    </div>
                    <span className="text-sm font-semibold text-red-500 dark:text-red-400">{t("settings_high_alert")}</span>
                  </div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">{t("settings_growth_threshold")}</label>
                  <input type="number" value={thresholds.highAlert} onChange={(e) => setThresholds({ ...thresholds, highAlert: Number(e.target.value) })} disabled={isViewOnly} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500 disabled:opacity-50" />
                </div>
                <div className="rounded-lg border border-amber-300 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-amber-500/20">
                      <i className="ri-alert-line text-amber text-xs" />
                    </div>
                    <span className="text-sm font-semibold text-amber">{t("settings_medium_alert")}</span>
                  </div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">{t("settings_growth_threshold")}</label>
                  <input type="number" value={thresholds.mediumAlert} onChange={(e) => setThresholds({ ...thresholds, mediumAlert: Number(e.target.value) })} disabled={isViewOnly} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber disabled:opacity-50" />
                </div>
                <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-emerald-500/20">
                      <i className="ri-check-line text-emerald text-xs" />
                    </div>
                    <span className="text-sm font-semibold text-emerald">{t("settings_low_normal")}</span>
                  </div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">{t("settings_below_threshold")}</label>
                  <input type="number" value={thresholds.lowAlert} disabled className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm opacity-50" />
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800/60 p-4">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{t("settings_threshold_viz")}</p>
                <div className="relative h-8 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800 flex">
                  <div className="bg-emerald/30 flex items-center justify-center text-[10px] font-semibold text-emerald" style={{ width: `${thresholds.mediumAlert}%` }}>{t("settings_viz_normal")}</div>
                  <div className="bg-amber/30 flex items-center justify-center text-[10px] font-semibold text-amber" style={{ width: `${thresholds.highAlert - thresholds.mediumAlert}%` }}>{t("settings_viz_medium")}</div>
                  <div className="bg-red-500/20 flex items-center justify-center text-[10px] font-semibold text-red-500 dark:text-red-400" style={{ width: `${Math.max(0, 100 - thresholds.highAlert)}%` }}>{t("settings_viz_high")}</div>
                </div>
                {!isViewOnly && (
                  <div className="flex items-center justify-end gap-2 pt-4">
                    <BtnGhost onClick={() => setThresholds(initialThresholds)}>{t("settings_reset")}</BtnGhost>
                    <BtnPrimary onClick={saveThresholds}>{t("settings_save")}</BtnPrimary>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Users ── */}
          {activeSection === "users" && (
            <UsersSection
              users={users}
              modal={modal}
              setModal={setModal}
              onSave={handleSaveUser}
              onSuspend={handleSuspendUser}
              showToast={showToast}
            />
          )}

          {/* ── Districts ── */}
          {activeSection === "districts" && (
            <DistrictsSection
              modal={modal}
              setModal={setModal}
              showToast={showToast}
            />
          )}

          {/* ── Organizations ── */}
          {activeSection === "organizations" && (
            <OrganizationsSection
              modal={modal}
              setModal={setModal}
              showToast={showToast}
            />
          )}

          {/* ── Cases ── */}
          {activeSection === "cases" && (
            <CasesSection
              modal={modal}
              setModal={setModal}
              showToast={showToast}
            />
          )}

          {/* ── API Keys ── */}
          {activeSection === "apikeys" && (
            <ApiKeysSection
              modal={modal}
              setModal={setModal}
              showToast={showToast}
            />
          )}

          {/* ── Health ── */}
          {activeSection === "health" && (
            <HealthSection showToast={showToast} />
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium animate-slide-up ${toast.type === "success" ? "bg-white dark:bg-slate-900 border-teal/30 text-teal" : "bg-white dark:bg-slate-900 border-red-500/30 text-red-500 dark:text-red-400"}`}>
          <div className="flex items-center gap-2">
            <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}